/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { IDataTableProps, IHeadingsProperty, ITableItemProps, ITableRowProps, Table, TableDataType } from '@msdyn365-commerce-modules/utilities';
import MsDyn365, {ICoreContext, IDateFormatOptions, IRequestContext} from '@msdyn365-commerce/core';
import { BusinessPartnerOperationRequest, BusinessPartnerUser } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { IB2bRequestsStatusData } from '../b2b-requests-status.data';
import { IB2bRequestsStatusProps } from '../b2b-requests-status.props.autogenerated';

export interface IB2bRequestsTableProps {
    moduleProps: IB2bRequestsStatusProps<IB2bRequestsStatusData>;
    isMobile: boolean;
    mobileExcludedColumns: string[];
}

export interface IB2bRequestsTableResources {
    b2bRequestsTypeCreateProspect: string;
    b2bRequestsTypeAddUser: string;
    b2bRequestsTypeEditUser: string;
    b2bRequestsTypeDeleteUser: string;
    b2bRequestsTypeRequestAccountStatement: string;
    b2bRequestsTypeRequestInvoiceCopy: string;
    b2bRequestsTypeUnknown: string;
    b2bRequestsStatusRequested: string;
    b2bRequestsStatusProcessed: string;
    b2bRequestsStatusError: string;
    b2bRequestsNumberOfItems: string;
    b2bRequestsNumberOfItemsSingular: string;
    b2bRequestsNameDisplay: string;
    b2bRequestsPreviousText: string;
    b2bRequestsNextText: string;
    b2bRequestsUserColumn: string;
    b2bRequestsStatusColumn: string;
    b2bRequestsRequestDateColumn: string;
    b2bRequestsTypeColumn: string;
    b2bRequestsDescriptionColumn: string;
}

export enum RequestsTableColumnHeader {
    Name = 'User',
    Status = 'Status',
    RequestDate = 'Request date',
    Type = 'Request type',
    Description = 'Request detail'
}

const getCurrentUrl = (reqContext: IRequestContext): URL=> {
    if (MsDyn365.isBrowser) {
        return new URL(window.location.href);
    } else {
        // NOTE: Typing on requestURL is incorrect
        if (reqContext.url.requestUrl.href) {
            return new URL(reqContext.url.requestUrl.href);
        } else {
            return new URL(reqContext.url.requestUrl.href);
        }
    }
};

const getUserName = (user: BusinessPartnerUser | undefined, resources: IB2bRequestsTableResources) => {
    if (!user || (!user.FirstName && !user.LastName)) {
        return '';
    }

    if (user.FirstName && !user.LastName) {
        return user.FirstName;
    } else if (user.LastName && !user.FirstName) {
        return user.LastName;
    } else {
        return resources.b2bRequestsNameDisplay
        .replace('{0}', user.FirstName!)
        .replace('{1}', user.LastName!);
    }
};

const createTableItems = (operationRequests: BusinessPartnerOperationRequest[] | undefined, props: IB2bRequestsStatusProps<IB2bRequestsStatusData>): ITableRowProps[] => {

    if (!ArrayExtensions.hasElements(operationRequests)) {
        return [];
    }

    return operationRequests.map((request) => {

        return {
            row: [
                {
                    id: props.resources.b2bRequestsTypeColumn,
                    type: TableDataType.Text,
                    value: getDisplayFriendlyTypeValue(request.TypeValue || 0, props.resources)
                } as ITableItemProps,
                {
                    id: props.resources.b2bRequestsRequestDateColumn,
                    type: TableDataType.Text,
                    value: formatDate(props.context, request.CreatedDateTime)
                } as ITableItemProps,
                {
                    id: props.resources.b2bRequestsDescriptionColumn,
                    type: TableDataType.Text,
                    value: ''
                } as ITableItemProps,
                {
                    id: props.resources.b2bRequestsUserColumn,
                    type: TableDataType.Text,
                    value: getUserName(request.B2bUser, props.resources)
                } as ITableItemProps,
                {
                    id: props.resources.b2bRequestsStatusColumn,
                    type: TableDataType.Text,
                    value: getDisplayFriendlyStatusValue(request.StatusValue || 0, props.resources)
                } as ITableItemProps
            ],
            isSelected: false,
            className: props.config.className
        };
    });
};

const formatDate = (context: ICoreContext, date: Date | undefined) => {
    if (date === undefined) {
        return '';
    }

    const dateOptions: IDateFormatOptions = { year: 'numeric', month: 'long', day: 'numeric'};
    return context.cultureFormatter.formatDate(date, dateOptions);
};

const getDisplayFriendlyTypeValue = (typeValue: number, resources: IB2bRequestsTableResources): string => {
    switch (typeValue) {
        case 1:
            return resources.b2bRequestsTypeCreateProspect;
        case 2:
            return resources.b2bRequestsTypeAddUser;
        case 3:
            return resources.b2bRequestsTypeDeleteUser;
        case 4:
            return resources.b2bRequestsTypeEditUser;
        case 5:
            return resources.b2bRequestsTypeRequestAccountStatement;
        case 6:
            return resources.b2bRequestsTypeRequestInvoiceCopy;
        default:
            return resources.b2bRequestsTypeUnknown;
    }
};

const getDisplayFriendlyStatusValue = (statusValue: number, resources: IB2bRequestsTableResources): string => {
    switch (statusValue) {
        case 1:
            return resources.b2bRequestsStatusProcessed;
        case 2:
            return resources.b2bRequestsStatusError;
        default:
            return resources.b2bRequestsStatusRequested;
    }
};

const createTableProps = (tableProps: IB2bRequestsTableProps) => {
    const { context, data, resources } = tableProps.moduleProps;

    const tableData = createTableItems(data.operationRequests.result, tableProps.moduleProps);
    const tableHeading = ArrayExtensions.hasElements(tableData) && tableData[0].row.filter((item) => {
        // Check that this is either not mobile or the column should be displayed when mobile.
        return !tableProps.isMobile || !tableProps.mobileExcludedColumns.includes(item.id);
    }).map((item) => {
            return {
                name: item.id,
                sortable: true
            } as IHeadingsProperty;
    }) || [];

    return {
        resources: {},
        headings: tableHeading,
        rows: tableData,
        className: `${tableProps.moduleProps.config.className}__table`,
        editLinkText: '',
        deleteLinkText: '',
        viewLinkText: '',
        enableToModify: false,
        actionLinkText: '',
        showCheckBoxes: false,
        isSortable: true,
        showPagination: true,
        minifyActions: tableProps.isMobile,
        excludedColumns: tableProps.isMobile ? tableProps.mobileExcludedColumns : [],
        paginationProperty: {
            skipCount: context?.request?.query?.skip && !isNaN(parseInt(context.request.query.skip, 10)) ? parseInt(context.request.query.skip, 10) : 0,
            itemPerPage: 10,
            prevText: resources.b2bRequestsPreviousText,
            nextText: resources.b2bRequestsNextText,
            paginationText: '',
            url: context && getCurrentUrl(context.request).href || ''
        },
        isSingleSelectOnly: true,
        actions: {
            onDelete: undefined,
            onEdit: undefined,
            onView: undefined
        }
    } as IDataTableProps;
};

export const B2bRequestsTable: React.FC<IB2bRequestsTableProps> = (tableProps: IB2bRequestsTableProps) => {
    return <Table {...createTableProps(tableProps)} />;
};
